import React from 'react';
import { useAuthorization } from 'Auth';

/**
 * Props for the CompanyLogoImage component
 */
export interface CompanyLogoImageProps {
  src: string;
  width?: string;
  height?: number;
  alt?: string;
  fallbackSrc?: string;
  style?: React.CSSProperties;
}

/**
 * Component that handles authenticated loading of company logos
 * Automatically fetches API endpoints with proper authentication headers
 * and converts them to blob URLs for display
 */
export const CompanyLogoImage: React.FC<CompanyLogoImageProps> = ({
  src,
  width,
  height,
  alt = 'Company Logo',
  fallbackSrc = '/sentry-logo.png',
  style,
}) => {
  const [token] = useAuthorization();
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    const fetchImage = async () => {
      if (!token) {
        setImageSrc(fallbackSrc);
        setLoading(false);
        return;
      }

      // If src is a static file (not an API endpoint), use it directly
      if (!src.startsWith('/api/')) {
        setImageSrc(src);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch(src, {
          headers: {
            authorization: `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const blob = await response.blob();
          const imageUrl = URL.createObjectURL(blob);
          setImageSrc(imageUrl);
        } else {
          setImageSrc(fallbackSrc);
        }
      } catch (err) {
        console.error('Error fetching logo:', err);
        setImageSrc(fallbackSrc);
      } finally {
        setLoading(false);
      }
    };

    fetchImage();

    // Cleanup function to revoke object URL
    return () => {
      if (imageSrc && imageSrc.startsWith('blob:')) {
        URL.revokeObjectURL(imageSrc);
      }
    };
  }, [src, token, fallbackSrc]);

  if (loading) {
    const loadingStyle: React.CSSProperties = {
      width: width || (height ? `${height}px` : '100px'),
      height: height ? `${height}px` : '50px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...style,
    };

    return (
      <div style={loadingStyle}>
        <div style={{ fontSize: '12px', color: '#999' }}>Loading...</div>
      </div>
    );
  }

  const imgStyle: React.CSSProperties = {
    ...style,
  };

  const imgProps: React.ImgHTMLAttributes<HTMLImageElement> = {
    src: imageSrc || fallbackSrc,
    alt,
    style: imgStyle,
  };

  if (width) imgProps.width = width;
  if (height) imgProps.height = height;

  return <img {...imgProps} />;
};
